import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { useSiteContent } from '../hooks/useSiteContent';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  // Session 213.78 — load editable footer content from CMS, with the
  // static defaults below kicking in when the admin hasn't overridden.
  const cms = useSiteContent('footer', {
    tagline: 'Your trusted partner in transportation insurance solutions. Specialized expertise for the trucking industry.',
    logo_url: '/assets/zion-logo.jpeg',
    contact_email: companyInfo?.email || 'info@zionuw.com',
    contact_phone: companyInfo?.phone || '',
    contact_address: companyInfo?.address || '',
    copyright: 'Zion Underwriting Partners',
  });

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#1e3a5f] text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="inline-block bg-white rounded-md p-2 mb-4 shadow-sm">
              <img
                src={cms.logo_url || '/assets/zion-logo.jpeg'}
                alt="Zion Underwriting Partners"
                className="h-12 w-auto object-contain"
                data-testid="footer-logo"
              />
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              {cms.tagline}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/"
                  onClick={scrollToTop}
                  className="text-gray-300 hover:text-[#9CA3AF] transition-colors text-sm"
                >
                  Home
                </Link>
              </li>
              <li>
                <button
                  onClick={() => {
                    const element = document.getElementById('services');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-gray-300 hover:text-[#9CA3AF] transition-colors text-sm"
                >
                  Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    const element = document.getElementById('about');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-gray-300 hover:text-[#9CA3AF] transition-colors text-sm"
                >
                  About Us
                </button>
              </li>
              <li>
                <Link
                  to="/quote"
                  className="text-gray-300 hover:text-[#9CA3AF] transition-colors text-sm"
                >
                  Get a Quote
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Our Services</h3>
            <ul className="space-y-2">
              <li className="text-gray-300 text-sm">Auto Physical Damage</li>
              <li className="text-gray-300 text-sm">Motor Truck Cargo</li>
              <li className="text-gray-300 text-sm">Non-Trucking Liability</li>
              <li className="text-gray-300 text-sm">Trucker's General Liability</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-[#2d9b9b] flex-shrink-0 mt-0.5" />
                <a
                  href={`tel:${cms.contact_phone || companyInfo.phone}`}
                  className="text-gray-300 hover:text-[#9CA3AF] transition-colors text-sm"
                  data-testid="footer-contact-phone"
                >
                  {cms.contact_phone || companyInfo.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-[#9CA3AF] flex-shrink-0 mt-0.5" />
                <a
                  href={`mailto:${cms.contact_email || companyInfo.email}`}
                  className="text-gray-300 hover:text-[#9CA3AF] transition-colors text-sm"
                  data-testid="footer-contact-email"
                >
                  {cms.contact_email || companyInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#9CA3AF] flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 text-sm" data-testid="footer-contact-address">{cms.contact_address || companyInfo.address}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} {cms.copyright || companyInfo.name}. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link
                to="/privacy-policy"
                className="text-gray-400 hover:text-[#9CA3AF] transition-colors text-sm"
                data-testid="footer-privacy-link"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-service"
                className="text-gray-400 hover:text-[#9CA3AF] transition-colors text-sm"
                data-testid="footer-terms-link"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
